/**
 * Token storage for the web client. Access + refresh tokens persist in
 * localStorage so a reload keeps the session; the access token is also mirrored
 * in a module variable for synchronous reads by the api-client on every request.
 */

import type { AuthTokens } from '@kolectra/shared/schemas';

const ACCESS_KEY = 'kolectra.web.accessToken';
const REFRESH_KEY = 'kolectra.web.refreshToken';

let accessToken: string | null = null;

/** Hydrate the in-memory access token from storage (call once at startup). */
export function loadTokens(): { accessToken: string | null; refreshToken: string | null } {
  try {
    accessToken = localStorage.getItem(ACCESS_KEY);
    return { accessToken, refreshToken: localStorage.getItem(REFRESH_KEY) };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function setTokens(tokens: AuthTokens): void {
  accessToken = tokens.accessToken;
  try {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  } catch {
    /* storage unavailable — session lives in-memory only */
  }
}

export function clearTokens(): void {
  accessToken = null;
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}
